import React from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import LoadComponent from './components/LoadComponent';

/**
 * 动态路由配置
 * 使用动态加载组件，并将webpackChunkName设置为path字段值
 * 支持Chrome 52版本，优化PDA设备上的加载性能
 */

// 直接导入首页组件，提高首屏加载速度
import Workbench from './common/Workbench';

// 公共页面
const RemoteControl = LoadComponent(
  () => import(/* webpackChunkName: "RemoteControl" */ './common/RemoteControl')
);
const AirGroundRemoteControl = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "AirGroundRemoteControl" */ './common/AirGroundRemoteControl'
    )
);

const Layout = LoadComponent(
  () => import(/* webpackChunkName: "Layout" */ './layouts')
);

const SelectStopPage = LoadComponent(
  () => import(/* webpackChunkName: "SelectStop" */ './common/SelectStop')
);

const IssueFeedback = LoadComponent(
  () => import(/* webpackChunkName: "IssueFeedback" */ './common/IssueFeedback')
);
const HistoryIssueOrders = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "HistoryIssueOrders" */ './common/HistoryIssueOrders'
    )
);
const VehicleDocuments = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "VehicleDocuments" */ './common/VehicleDocuments'
    )
);

const Settings = LoadComponent(
  () =>
    import(/* webpackChunkName: "settings" */ './general-transport/Settings')
);

const TaskRecord = LoadComponent(
  () => import(/* webpackChunkName: "newTaskRecord" */ './common/TaskRecord')
);

const ScanCodeToRemoteControl = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "ScanCodeToRemoteControl" */ './common/ScanCodeToRemoteControl'
    )
);

// air-ground页面
const AirGroundSingleVehicle = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "airGroundSingleVehicle" */ './air-ground/SingleVehicle'
    )
);
const AirGroundScanPickup = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "airGroundScanPickup" */ './air-ground/ScanPickup'
    )
);

// 点对点接驳页面
const GeneralTransportSingleVehicle = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "newSingleVehicle" */ './general-transport/SingleVehicle'
    )
);

const GeneralTransportCreateTask = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "createTask" */ './general-transport/CreateTask'
    )
);
const GeneralTransportPackageDetail = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "newPackageDetail" */ './general-transport/PackageDetail'
    )
);
const GeneralTransportScan = LoadComponent(
  () => import(/* webpackChunkName: "NewScan" */ './general-transport/Scan')
);
const GeneralTransportRemovePackage = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "NewRemovePackage" */ './general-transport/RemovePackage'
    )
);
const GeneralTransportManual = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "NewManual" */ './general-transport/ManualInput'
    )
);

const GeneralTransportTaskTrace = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "newTaskTrace" */ './general-transport/TaskTrace'
    )
);
const GeneralTransportChooseRecipient = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "ChooseRecipient" */ './general-transport/ChooseRecipient'
    )
);

// 蛛网页面
const SpiderWebSingleVehicle = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "newSingleVehicle" */ './spider-web/SingleVehicle'
    )
);
const SpiderWebCreateTask = LoadComponent(
  () =>
    import(/* webpackChunkName: "newCreateTask" */ './spider-web/CreateTask')
);
const SpiderWebScan = LoadComponent(
  () => import(/* webpackChunkName: "SpiderWebScan" */ './spider-web/Scan')
);

const SpiderWebRemovePackage = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "SpiderWebRemovePackage" */ './spider-web/RemovePackage'
    )
);
const SpiderWebManualInput = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "SpiderWebManualInput" */ './spider-web/ManualInput'
    )
);
const SpiderWebStationSequenceEdit = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "SpiderWebStationSequenceEdit" */ './spider-web/StationSequenceEdit'
    )
);
const SpiderWebPackageDetail = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "SpiderWebPackageDetail" */ './spider-web/PackageDetail'
    )
);
const SpiderWebTaskTrace = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "SpiderWebTaskTrace" */ './spider-web/TaskTrace'
    )
);

// jkc页面
const JkcScan = LoadComponent(
  () => import(/* webpackChunkName: "JkcScan" */ './jkc/Scan')
);
const JkcPackageDetail = LoadComponent(
  () => import(/* webpackChunkName: "JkcPackageDetail" */ './jkc/PackageDetail')
);
const JkcRemovePackage = LoadComponent(
  () => import(/* webpackChunkName: "JkcRemovePackage" */ './jkc/RemovePackage')
);
const JkcManualInput = LoadComponent(
  () => import(/* webpackChunkName: "JkcManualInput" */ './jkc/ManualInput')
);
const JkcHistoryOrders = LoadComponent(
  () => import(/* webpackChunkName: "JkcHistoryOrders" */ './jkc/HistoryOrders')
);
const JkcHistoryOrderDetail = LoadComponent(
  () =>
    import(
      /* webpackChunkName: "JkcHistoryOrderDetail" */ './jkc/HistoryOrderDetail'
    )
);
const JkcOrderDetail = LoadComponent(
  () => import(/* webpackChunkName: "JkcOrderDetail" */ './jkc/OrderDetail')
);
const SelectPosition = LoadComponent(
  () => import(/* webpackChunkName: "SelectPosition" */ './jkc/SelectPosition')
);

const routes: RouteObject[] = [
  {
    path: '/',
    children: [
      { index: true, element: <Navigate to="/workbench" /> },
      { path: 'workbench', element: <Workbench /> },
      { path: 'remoteControl', element: <RemoteControl /> },
      { path: 'airGroundRemoteControl', element: <AirGroundRemoteControl /> }
    ]
  },
  {
    path: '/common',
    element: <Layout />,
    children: [
      { path: 'selectStopPage', element: <SelectStopPage /> },
      { path: 'taskRecord', element: <TaskRecord /> },
      { path: 'issueFeedback', element: <IssueFeedback /> },
      { path: 'historyIssueOrders', element: <HistoryIssueOrders /> },
      { path: 'vehicleDocuments', element: <VehicleDocuments /> },
      { path: 'scanCodeToRemoteControl', element: <ScanCodeToRemoteControl /> }
    ]
  },
  {
    path: '/air-ground',
    element: <Layout />,
    children: [
      { path: 'singleVehicle', element: <AirGroundSingleVehicle /> },
      { path: 'scanPickup', element: <AirGroundScanPickup /> }
    ]
  },
  {
    path: '/general-transport',
    element: <Layout />,
    children: [
      { path: 'singleVehicle', element: <GeneralTransportSingleVehicle /> },
      { path: 'taskTrace', element: <GeneralTransportTaskTrace /> },
      { path: 'settings', element: <Settings /> },
      { path: 'createTask', element: <GeneralTransportCreateTask /> },
      { path: 'packageDetail', element: <GeneralTransportPackageDetail /> },
      { path: 'scan', element: <GeneralTransportScan /> },
      { path: 'manual', element: <GeneralTransportManual /> },
      { path: 'removePackage', element: <GeneralTransportRemovePackage /> },
      { path: 'chooseRecipient', element: <GeneralTransportChooseRecipient /> }
    ]
  },
  {
    path: '/spider-web',
    element: <Layout />,
    children: [
      { path: 'singleVehicle', element: <SpiderWebSingleVehicle /> },
      { path: 'taskTrace', element: <SpiderWebTaskTrace /> },
      { path: 'createTask', element: <SpiderWebCreateTask /> },
      { path: 'scan', element: <SpiderWebScan /> },
      { path: 'removePackage', element: <SpiderWebRemovePackage /> },
      { path: 'manual', element: <SpiderWebManualInput /> },
      {
        path: 'stationSequenceEdit',
        element: <SpiderWebStationSequenceEdit />
      },
      { path: 'packageDetail', element: <SpiderWebPackageDetail /> }
    ]
  },
  {
    path: '/jkc',
    element: <Layout />,
    children: [
      { path: 'scan', element: <JkcScan /> },
      { path: 'packageDetail', element: <JkcPackageDetail /> },
      { path: 'removePackage', element: <JkcRemovePackage /> },
      { path: 'manual', element: <JkcManualInput /> },
      { path: 'selectPosition', element: <SelectPosition /> },
      { path: 'historyOrders', element: <JkcHistoryOrders /> },
      { path: 'historyOrderDetail', element: <JkcHistoryOrderDetail /> },
      { path: 'orderDetail', element: <JkcOrderDetail /> }
    ]
  }
];

export default routes;
